import { useMemo, useCallback } from "react";
import { useInfiniteQuery } from "@tanstack/react-query";
import { getAdminEventOccasions } from "../api/occasionApi";
import type { EventOccasion } from "../types";

export interface UseEventOccasionsListParams {
	pageSize?: number;
	isPublished?: boolean;
	keyword?: string;
}

export interface UseEventOccasionsListResult {
	occasions: EventOccasion[];
	loading: boolean;
	isFetchingNextPage: boolean;
	fetchNextPage: () => void;
	hasNextPage: boolean;
	refetch: () => void;
}

export interface Event {
	id: string | number;
	title: string;
	image?: string | null;
	gallery?: string[];
	isDisplayed: boolean;
	maxCapacity: number;
	sumExpected: number;
	sumAttended?: number;
	startDate?: string | null;
	endDate?: string | null;
	start?: string | null;
	end?: string | null;
	isSettingTime?: boolean;
	checkIn?: string;
	timeOverlap?: boolean;
	allowMultipleBookings?: boolean;
	price?: number | null;
	currency?: string;
	customReservationMessage?: boolean;
	customCancelSettings?: boolean;
	programTitle?: string;
}

interface OccasionImageData {
	imageUrl: string | undefined;
	gallery: string[];
}

const resolveOccasionImages = (occasion: any): OccasionImageData => {
	if (occasion.occasionImages && occasion.occasionImages.length > 0) {
		const firstImage = occasion.occasionImages[0];
		const imageUrl = firstImage?.picUrl || firstImage?.preview;
		const gallery = occasion.occasionImages
			.map((img: any) => img?.picUrl || img?.preview)
			.filter(Boolean);
		return { imageUrl, gallery };
	}

	if (occasion.heroImageUrl) {
		const imageUrl = occasion.heroImageUrl;
		let gallery = occasion.gallery || [];
		if (imageUrl && !gallery.includes(imageUrl)) {
			gallery = [imageUrl, ...gallery];
		}
		return { imageUrl, gallery };
	}

	if (occasion.gallery && occasion.gallery.length > 0) {
		return {
			imageUrl: occasion.gallery[0],
			gallery: occasion.gallery,
		};
	}

	return { imageUrl: undefined, gallery: [] };
};

const transformOccasionToEvent = (occasion: any): Event => {
	const imageData = resolveOccasionImages(occasion);

	return {
		id: occasion.eventOccasionId || occasion.occasionId || occasion.id,
		title: occasion.title || "Untitled Event",
		image: imageData.imageUrl || null,
		gallery: imageData.gallery,
		isDisplayed: occasion.isPublished ?? occasion.isDisplayed ?? false,
		maxCapacity:
			occasion.maxCapacity ||
			occasion.maximumAttendees ||
			occasion.totalMaxCapacity ||
			0,
		sumExpected:
			occasion.sumExpected ||
			occasion.reservationCount ||
			occasion.totalReservations ||
			0,
		startDate: occasion.reservationStartAt || occasion.startDate || null,
		endDate: occasion.reservationEndAt || occasion.endDate || null,
		start: occasion.start || occasion.earliestOccurrenceStart || null,
		end: occasion.end || occasion.latestOccurrenceEnd || null,
		isSettingTime: Boolean(
			occasion.reservationStartAt ||
				occasion.reservationEndAt ||
				occasion.isSettingTime,
		),
		checkIn: occasion.checkIn || "QR",
		timeOverlap: occasion.timeOverlap ?? false,
		allowMultipleBookings: occasion.allowMultipleBookings ?? false,
		price: occasion.price ?? null,
		currency: occasion.currency || "JPY",
		customReservationMessage: occasion.customReservationMessage ?? false,
		customCancelSettings: occasion.customCancelSettings ?? false,
		programTitle: occasion.programTitle || "－",
	};
};

export const useEventOccasionsList = (
	params?: UseEventOccasionsListParams,
): UseEventOccasionsListResult => {
	const pageSize = params?.pageSize || 50;

	const {
		data,
		isLoading,
		isFetchingNextPage,
		fetchNextPage: fetchNext,
		hasNextPage: hasMore,
		refetch: queryRefetch,
	} = useInfiniteQuery({
		queryKey: [
			"eventOccasionsList",
			params?.isPublished,
			params?.keyword,
			pageSize,
		],
		queryFn: async ({ pageParam }) => {
			const response = await getAdminEventOccasions({
				page: pageParam,
				pageSize: pageSize,
				isPublished: params?.isPublished,
				keyword: params?.keyword,
			});
			return response;
		},
		initialPageParam: 1,
		getNextPageParam: (lastPage) => {
			if (lastPage.pagination.page < lastPage.pagination.totalPages) {
				return lastPage.pagination.page + 1;
			}
			return undefined;
		},
	});

	const occasions = useMemo(() => {
		if (!data?.pages) return [];
		return data.pages.flatMap((page) =>
			page.data.map(transformOccasionToEvent),
		);
	}, [data]);

	const fetchNextPage = useCallback(() => {
		if (hasMore && !isFetchingNextPage) {
			fetchNext();
		}
	}, [hasMore, isFetchingNextPage, fetchNext]);

	const refetch = useCallback(async () => {
		await queryRefetch();
	}, [queryRefetch]);

	return {
		occasions,
		loading: isLoading,
		isFetchingNextPage,
		fetchNextPage,
		hasNextPage: hasMore ?? false,
		refetch,
	};
};
